/**
 * `window.PFModal` — the modal API the EJS pages' scripts use, for the SPA.
 *
 * It exists because the DOM contract is shared: the browser suite waits for
 * `window.PFModal && typeof window.PFModal.open === 'function'` before it clicks a control that opens a
 * dialog, which is how it avoids clicking into the gap between markup and the script that binds it. A page
 * that has the button but not the API fails that wait, and the failure reads as "the dialog never opened".
 *
 * React owns whether a dialog is shown (its `open` prop), so a dialog registers its own setter here and this
 * module stays a registry with no state of its own.
 */
type Handler = { open: () => void; close: () => void };

const registry = new Map<string, Handler>();

export function registerModal(id: string, handler: Handler): void {
  registry.set(id, handler);
}

export function unregisterModal(id: string): void {
  registry.delete(id);
}

const api = {
  open: (id: string) => registry.get(id)?.open(),
  close: (id: string) => registry.get(id)?.close(),
  register: registerModal,
  unregister: unregisterModal,
};

declare global {
  interface Window {
    PFModal?: typeof api;
  }
}

if (typeof window !== 'undefined' && !window.PFModal) window.PFModal = api;
